import { ChartResult, formatNumber } from '@employ-engage/data';
import { useTranslation } from 'react-i18next';
import { InfoNote } from '../info-note/info-note';

/**
 * How a figure has moved since an earlier window — or why it cannot say.
 *
 * Three states, and the two that are not a number matter as much as the one
 * that is:
 *
 * - **absent** — nothing asked for a comparison. Draw nothing; an empty space
 *   is correct.
 * - **withheld** — a comparison was asked for and cannot be given, because the
 *   earlier figure was too small to publish. Say so. Drawing nothing here
 *   would be indistinguishable from "it did not move", which is a claim
 *   nobody made.
 * - **present** — there is a difference, with an interval around it.
 *
 * ## Two decisions that shape this
 *
 * **'unclear' does not get an arrow.** At the sample sizes here — departments
 * of 15 to 30 — an interval containing zero is the honest reading of most
 * comparisons. Rendering every wobble as an arrow with a number teaches
 * people to act on noise, and the arrow is the part that gets remembered. It
 * reads "about the same" instead, with the figures behind the ⓘ for anyone
 * who wants them.
 *
 * **Direction is not coloured.** Up is not good. On a bottom-box measure —
 * the share answering at the bad end of the scale — a rise is the worst news
 * on the page, and green would say the opposite. The component cannot know
 * which way is better, so it says which way it moved and leaves the judgement
 * to the reader.
 */

export type DeltaState = 'absent' | 'withheld' | 'present';

export interface StatDeltaProps {
  result?: ChartResult | null;
  /** Which cell the figure came from. A stat has exactly one. */
  cellIndex?: number;
  /** 'points' for an average on its own scale, 'percentagePoints' otherwise. */
  units?: 'points' | 'percentagePoints';
  className?: string;
}

export const deltaStateOf = (
  result: ChartResult | null | undefined,
  cellIndex = 0
): DeltaState => {
  if (!result?.meta?.comparison) return 'absent';
  return result.cells?.[cellIndex]?.comparison ? 'present' : 'withheld';
};

export function StatDelta({
  result,
  cellIndex = 0,
  units = 'percentagePoints',
  className,
}: StatDeltaProps) {
  const { t } = useTranslation();

  const state = deltaStateOf(result, cellIndex);
  if (state === 'absent') return null;

  const meta = result?.meta?.comparison;
  const earlier =
    meta?.mode === 'previousYear'
      ? t('a year earlier')
      : t('the previous period');

  const wrapper = `mt-1 flex items-center justify-center gap-1 text-xs text-light-secondaryText dark:text-dark-secondaryText ${
    className ?? ''
  }`;

  if (state === 'withheld') {
    return (
      <div className={wrapper}>
        <span>{t('No comparison available')}</span>
        <InfoNote label={t('Why there is no comparison')}>
          {meta?.reason ??
            t(
              'Too few responses {{earlier}} to publish that figure, and a change would give it away: subtract the change from this number and you have the one that was withheld.',
              { earlier }
            )}
        </InfoNote>
      </div>
    );
  }

  const comparison = result!.cells[cellIndex].comparison!;
  const size = formatNumber(
    Math.abs(comparison.delta),
    units === 'points' ? 'decimal1' : 'integer'
  );
  const unitLabel =
    units === 'points' ? t('points') : t('percentage points');

  const range = t('Somewhere between {{low}} and {{high}} {{unit}}.', {
    low: formatNumber(comparison.low, units === 'points' ? 'decimal1' : 'integer'),
    high: formatNumber(comparison.high, units === 'points' ? 'decimal1' : 'integer'),
    unit: unitLabel,
  });

  if (comparison.direction === 'unclear') {
    return (
      <div className={wrapper}>
        <span>{t('About the same as {{earlier}}', { earlier })}</span>
        <InfoNote label={t('What this comparison can and cannot say')}>
          {t(
            'The difference is {{size}} {{unit}}, but with this many responses that is within what chance alone would produce, so it cannot be told apart from no change. {{range}}',
            { size, unit: unitLabel, range }
          )}
        </InfoNote>
      </div>
    );
  }

  const up = comparison.direction === 'up';
  return (
    <div className={wrapper}>
      {/* A glyph rather than a colour: the component cannot know whether up is
          good, and on a bottom-box measure it is not. */}
      <i
        className={up ? 'fa-solid fa-arrow-up' : 'fa-solid fa-arrow-down'}
        aria-hidden
      />
      <span>
        {up
          ? t('{{size}} {{unit}} higher than {{earlier}}', {
              size,
              unit: unitLabel,
              earlier,
            })
          : t('{{size}} {{unit}} lower than {{earlier}}', {
              size,
              unit: unitLabel,
              earlier,
            })}
      </span>
      <InfoNote label={t('How certain this comparison is')}>{range}</InfoNote>
    </div>
  );
}

export default StatDelta;
